/** Shared types for homepage + sponsorship surfaces. */

import type { HomeClubShortcut, HomeCommunityFeedItem } from '../../types/communityPosts';

export interface HomeVehicleCard {
  id: number;
  year: number;
  make: string;
  model: string;
  trim?: string;
  nickname?: string | null;
  description?: string | null;
  display_name?: string;
  owner_username: string;
  owner_display_name?: string;
  public_slug?: string | null;
  image_url?: string | null;
  modification_count?: number;
  chassis?: string | null;
  engine?: string | null;
  drivetrain?: string | null;
  created_at?: string;
  updated_at?: string;
  badges?: unknown[];
  badge_overflow?: number;
  is_public?: boolean;
  favorite_count?: number;
  is_favorited?: boolean;
  like_count?: number;
  user_has_liked?: boolean;
  view_count?: number;
}

export interface FeaturedBuild {
  id?: number;
  vehicle_id: number;
  blurb?: string | null;
  curator_label?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
  vehicle: HomeVehicleCard;
}

export interface ProductUpdate {
  id: number;
  title: string;
  body?: string | null;
  href?: string | null;
  published_at?: string | null;
}

/** Homepage social-proof blurb (admin-managed via home_testimonials). */
export interface HomeTestimonial {
  id: number;
  quote: string;
  author_name: string;
  author_handle?: string | null;
  vehicle_label?: string | null;
  rating?: number;
  avatar_url?: string | null;
  source?: string;
  status?: 'draft' | 'active' | 'archived' | string;
  sort_order?: number;
  is_placeholder?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdCreative {
  campaign_id: number;
  creative_id: number;
  placement: string;
  advertiser_name: string;
  headline: string;
  body?: string | null;
  cta_label?: string | null;
  image_url?: string | null;
  alt_text?: string | null;
  click_url: string;
}

export interface HomeSponsorship {
  creative: AdCreative;
  placement?: string;
}

export interface HomeNextAction {
  id: string;
  title: string;
  body?: string | null;
  cta_label: string;
  href: string;
  urgency?: 'high' | 'medium' | 'low';
}

/** Accepts API rows shaped as { title, cta_label } or legacy { label }. */
export function normalizeNextAction(raw: unknown): HomeNextAction | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const title = String(row.title ?? row.label ?? '').trim();
  const href = String(row.href ?? '').trim();
  if (!title || !href) return null;
  const cta = String(row.cta_label ?? row.cta ?? 'Continue').trim() || 'Continue';
  return {
    id: String(row.id ?? href),
    title,
    body: row.body == null || row.body === '' ? null : String(row.body),
    cta_label: cta,
    href,
    urgency: row.urgency as HomeNextAction['urgency'],
  };
}

export function normalizeNextActions(raw: unknown): HomeNextAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeNextAction).filter((a): a is HomeNextAction => a !== null);
}

export interface HomeRoleAlert {
  id: string;
  role: 'organizer' | 'judge' | 'club_admin' | string;
  title: string;
  body?: string | null;
  cta_label: string;
  href: string;
  urgency?: number;
}

export interface HomeRecognitionItem {
  id: string | number;
  title: string;
  subtitle?: string | null;
  awarded_at?: string | null;
  vehicle?: HomeVehicleCard | null;
  href?: string | null;
}

/** Count summary from homeRecognitionSummary (not a list of awards). */
export interface HomeRecognitionSummary {
  award_count?: number;
  badge_count?: number;
}

export interface HomeUpcomingEvent {
  id: number;
  title: string;
  slug: string;
  starts_at?: string | null;
  ends_at?: string | null;
  location_label?: string | null;
  role?: 'organizer' | 'judge' | 'registrant' | string;
  href?: string | null;
}

export interface GarageSnapshotData {
  vehicles: HomeVehicleCard[];
  maturity?: 'empty' | 'incomplete' | 'mature';
  primary_vehicle_id?: number | null;
}

export interface PublicHomeData {
  featured_build?: FeaturedBuild | null;
  recent_builds?: HomeVehicleCard[];
  recently_awarded?: HomeVehicleCard[];
  recognized_builds?: HomeVehicleCard[];
  product_updates?: ProductUpdate[];
  testimonials?: HomeTestimonial[] | null;
  sponsorship?: HomeSponsorship | AdCreative | null;
}

export interface AuthenticatedHomeData {
  greeting?: string | null;
  garage?: GarageSnapshotData | null;
  next_actions?: HomeNextAction[];
  role_alerts?: HomeRoleAlert[];
  featured_build?: FeaturedBuild | null;
  recognition?: HomeRecognitionSummary | HomeRecognitionItem[] | null;
  upcoming_events?: HomeUpcomingEvent[];
  /** @deprecated Personal home no longer surfaces discovery rails. */
  recent_builds?: HomeVehicleCard[] | null;
  /** @deprecated Personal home no longer surfaces discovery rails. */
  recognized_builds?: HomeVehicleCard[] | null;
  popular_builds?: HomeVehicleCard[] | null;
  popular_this_week?: HomeVehicleCard[] | null;
  favorite_builds?: HomeVehicleCard[] | null;
  my_clubs?: HomeClubShortcut[] | null;
  community_feed?: HomeCommunityFeedItem[] | null;
  product_updates?: ProductUpdate[];
  sponsorship?: HomeSponsorship | AdCreative | null;
}

export type DiscoverGridItem =
  | { kind: 'vehicle'; vehicle: HomeVehicleCard | Record<string, unknown> }
  | { kind: 'sponsored'; creative: AdCreative };

export function normalizeSponsorship(
  value: HomeSponsorship | AdCreative | null | undefined
): AdCreative | null {
  if (!value) return null;
  if ('creative' in value && value.creative) return value.creative;
  if ('creative_id' in value && 'campaign_id' in value) return value as AdCreative;
  return null;
}

export function homeVehicleToCardProps(v: HomeVehicleCard) {
  return {
    id: v.id,
    year: v.year,
    make: v.make,
    model: v.model,
    trim: v.trim || '',
    nickname: v.nickname || undefined,
    description: v.description || undefined,
    modification_count: v.modification_count || 0,
    display_name: v.display_name || `${v.year} ${v.make} ${v.model}`,
    owner_username: v.owner_username,
    owner_display_name: v.owner_display_name,
    public_slug: v.public_slug ?? null,
    image_url: v.image_url || undefined,
    is_public: v.is_public,
    created_at: v.created_at || '',
    updated_at: v.updated_at,
    chassis: v.chassis,
    engine: v.engine,
    drivetrain: v.drivetrain,
    favorite_count: v.favorite_count,
    is_favorited: v.is_favorited,
    like_count: v.like_count,
    user_has_liked: v.user_has_liked,
    view_count: v.view_count,
  };
}
